<template>
	<Teleport to="body">
		<template v-for="container in toastContainers" :key="container.position">
			<TransitionGroup
				v-if="container.toasts.length"
				name="toast"
				tag="div"
				:class="bemm('container', [container.position])"
			>
				<div
					v-for="toast in container.toasts"
					:key="toast.id"
					:class="bemm('', ['', toast.type || ''])"
					:role="getRole(toast.type)"
					:aria-live="getAriaLive(toast.type)"
					:style="`--toast-color: var(--color-${toast.type})`"
					@mouseenter="pause(toast.id)"
					@mouseleave="resume(toast.id)"
				>
					<div :class="bemm('content')">
						<Icon v-if="toast.icon" :class="bemm('icon')" :name="toast.icon" />
						<div :class="bemm('body')">
							<div v-if="toast.title" :class="bemm('title')">
								{{ toast.title }}
							</div>
							<div :class="bemm('message')">
								{{ toast.message }}
							</div>
							<button
								v-if="toast.action"
								type="button"
								:class="bemm('action')"
								@click="handleAction(toast.id, toast.action.onClick)"
							>
								{{ toast.action.label }}
							</button>
						</div>
					</div>
					<button
						v-if="toast.dismissible"
						:class="bemm('close')"
						@click="dismiss(toast.id)"
						:aria-label="$t('common.aria.closeNotification')"
					>
						<Icon name="close" size="small" />
					</button>
				</div>
			</TransitionGroup>
		</template>
	</Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useBemm } from 'bemm';
import { ToastPosition, ToastType } from './Toast.model';
import { toastService } from './Toast.service';
import type { ToastInstance } from './Toast.service';
import type { PropType } from 'vue';

import { Icon } from '../Icon';

const bemm = useBemm('toast', { return: 'string', includeBaseClass: true });
const props = defineProps({
	position: {
		type: String as PropType<ToastPosition>,
		default: undefined,
	},
});

const toasts = computed(() => {
	return toastService.toasts.value;
});

const toastContainers = computed<
	{ position: ToastPosition; toasts: ToastInstance[] }[]
>(() =>
	(props.position ? [props.position] : Object.values(ToastPosition)).map(
		(position) => ({
			position,
			toasts: toasts.value.filter((toast) => toast.position === position),
		})
	)
);

const getRole = (type?: ToastType) => {
	return type === ToastType.ERROR ? 'alert' : 'status';
};

const getAriaLive = (type?: ToastType) => {
	return type === ToastType.ERROR ? 'assertive' : 'polite';
};

const dismiss = (id: string) => {
	toastService.hide(id);
};

const pause = (id: string) => {
	toastService.pause(id);
};

const resume = (id: string) => {
	toastService.resume(id);
};

const handleAction = (id: string, onClick: () => void) => {
	onClick();
	toastService.hide(id);
};
</script>

<style lang="scss">
.toast {
	&__container {
		position: fixed;
		z-index: 9999;
		pointer-events: none;
		display: flex;
		flex-direction: column;
		gap: var(--space-m);
		padding: var(--space-m);

		> * {
			pointer-events: auto;
		}

		&--top {
			top: 0;
			left: 50%;
			transform: translateX(-50%);
			align-items: center;
		}

		&--bottom {
			bottom: 0;
			left: 50%;
			transform: translateX(-50%);
			align-items: center;
			flex-direction: column-reverse;
		}

		&--top-left {
			top: 0;
			left: 0;
			align-items: flex-start;
		}

		&--top-right {
			top: 0;
			right: 0;
			align-items: flex-end;
		}

		&--bottom-left {
			bottom: 0;
			left: 0;
			align-items: flex-start;
			flex-direction: column-reverse;
		}

		&--bottom-right {
			bottom: 0;
			right: 0;
			align-items: flex-end;
			flex-direction: column-reverse;
		}
	}
}

.toast {
	--toast-border-color: color-mix(
		in srgb,
		var(--toast-color, var(--color-primary)),
		var(--color-background) 50%
	);
	--toast-background-color: color-mix(
		in srgb,
		var(--toast-color, var(--color-background)),
		var(--color-background) 90%
	);
	--toast-text-color: color-mix(
		in srgb,
		var(--toast-color, var(--color-foreground)),
		var(--color-foreground) 20%
	);

	display: flex;
	align-items: center;
	gap: var(--space-m);
	padding: var(--space-m);
	border-radius: var(--border-radius-l);
	max-width: 400px;
	width: fit-content;
	z-index: 9999;
	outline: 2px solid var(--toast-border-color);
	background-color: var(--toast-background-color);
	color: var(--toast-text-color);
	box-shadow: 2px 2px var(--space) rgba(0, 0, 0, 0.1);
	&-enter-active,
	&-leave-active {
		transition: all 0.3s ease;
	}

	&-enter-from {
		opacity: 0;
		transform: translateY(-20px);
	}

	&-leave-to {
		opacity: 0;
		transform: translateX(20px);
	}

	&--success {
		--toast-color: var(--color-success);
	}

	&--error {
		--toast-color: var(--color-error);
	}

	&--warning {
		--toast-color: var(--color-warning);
	}

	&--info {
		--toast-color: var(--color-primary);
	}

	&--default {
		--toast-color: var(--color-primary);
	}

	&__content {
		display: flex;
		align-items: flex-start;
		gap: var(--space-m);
		flex: 1;
	}

	&__icon {
		font-size: 1.25rem;
		color: var(--toast-color);
		flex-shrink: 0;
		margin-top: 2px;
	}

	&__body {
		flex: 1;
	}

	&__title {
		font-weight: var(--font-weight-bold);
		margin-bottom: var(--space-xs);
		color: var(--color-foreground);
	}

	&__message {
		line-height: 1.5;
	}

	&__action {
		margin-top: var(--space-s);
		padding: var(--space-xs) var(--space-s);
		background: var(--toast-color);
		color: var(--color-background);
		border: none;
		border-radius: var(--border-radius);
		font-size: var(--font-size-s);
		font-weight: var(--font-weight-medium);
		cursor: pointer;
		transition: opacity var(--transition-fast);

		&:hover {
			opacity: 0.85;
		}
	}

	&__close {
		flex-shrink: 0;
		background: none;
		border: none;
		padding: var(--space-xs);
		cursor: pointer;
		color: var(--toast-text-color);
		opacity: 0.7;
		transition: opacity var(--transition-fast);
		border-radius: var(--border-radius-s);

		&:hover {
			opacity: 1;
			background: color-mix(in srgb, var(--toast-color), transparent 90%);
		}
	}
}
</style>
